import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface WorkTimeWidgetProps {
  user: any;
}

interface WorkSessionData {
  sesionActiva: boolean;
  sessionId?: number;
  loginTime?: string;
  horasActuales: number;
  horasRegulares: number;
  horasExtra: number;
  necesitaAprobacion: boolean;
  estado: string;
  alertaEnviada: boolean;
  horasExtraAprobadas: boolean;
}

const WorkTimeWidget: React.FC<WorkTimeWidgetProps> = ({ user }) => {
  const [workData, setWorkData] = useState<WorkSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Solo mostrar para operadores y supervisores (no admin)
    if (['operador', 'supervisor'].includes(user?.role)) {
      loadWorkStatus();
      
      // Actualizar cada minuto
      const timer = setInterval(() => {
        setCurrentTime(new Date());
        loadWorkStatus();
      }, 60000); // 60 segundos
      
      return () => clearInterval(timer);
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadWorkStatus = async () => {
    try {
      // Usar endpoint específico del usuario para datos reales
      const response = await api.get(`/work-sessions/user-hours/${user.id}`);
      if (response.data.success) {
        const workResponseData = response.data.data;
        
        // Formatear datos para el widget
        const formattedData = {
          sesionActiva: workResponseData.sesionActiva || false,
          sessionId: workResponseData.sessionId || null,
          loginTime: workResponseData.loginTime || null,
          horasActuales: workResponseData.horasTrabajadas || 0,
          horasRegulares: workResponseData.horasRegulares || 0,
          horasExtra: workResponseData.horasExtra || 0,
          necesitaAprobacion: (workResponseData.horasTrabajadas || 0) > 8,
          estado: workResponseData.sesionActiva ? 'activa' : 'inactiva',
          alertaEnviada: (workResponseData.horasTrabajadas || 0) > 10,
          horasExtraAprobadas: workResponseData.horasExtraAprobadas || false
        };
        
        setWorkData(formattedData);
      }
    } catch (error) {
      console.error('Error cargando estado de trabajo:', error);
      // Fallback: generar datos basados en auditorías del usuario
      try {
        // Intentar calcular horas basadas en auditorías del mes
        const sueldoResponse = await api.get(`/sueldos?operadorId=${user.id}&mes=${new Date().getMonth() + 1}&anio=${new Date().getFullYear()}`);
        
        if (sueldoResponse.data.success && sueldoResponse.data.data && sueldoResponse.data.data.length > 0) {
          const sueldoInfo = sueldoResponse.data.data[0];
          const auditoriasHechas = parseInt(sueldoInfo.auditoriasHechas) || 0;
          const horasEstimadas = Math.max(auditoriasHechas * 1.5, user.role === 'admin' ? 6 : 8);
          
          setWorkData({
            sesionActiva: horasEstimadas > 0,
            sessionId: null,
            loginTime: null,
            horasActuales: horasEstimadas,
            horasRegulares: Math.min(horasEstimadas, 8),
            horasExtra: Math.max(0, horasEstimadas - 8),
            necesitaAprobacion: horasEstimadas > 8,
            estado: horasEstimadas > 0 ? 'activa' : 'inactiva',
            alertaEnviada: horasEstimadas > 10,
            horasExtraAprobadas: false
          });
          
        } else {
          // Ultimate fallback
          const horasDefault = user.role === 'admin' ? 7 : user.role === 'supervisor' ? 8.5 : 8;
          setWorkData({
            sesionActiva: false,
            sessionId: null,
            loginTime: null,
            horasActuales: horasDefault,
            horasRegulares: Math.min(horasDefault, 8),
            horasExtra: Math.max(0, horasDefault - 8),
            necesitaAprobacion: horasDefault > 8,
            estado: 'inactiva',
            alertaEnviada: false,
            horasExtraAprobadas: false
          });
        }
      } catch (fallbackError) {
        console.error('Error en fallback de trabajo:', fallbackError);
        setWorkData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (horas: number): string => {
    const totalMinutes = Math.floor(horas * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Función para determinar si hay irregularidades que requieren atención
  const hasIrregularities = () => {
    if (!workData) return false;
    
    // Mostrar si hay horas extra sin aprobar
    if (workData.horasExtra > 0 && !workData.horasExtraAprobadas) return true;
    
    // Mostrar si hay más de 10 horas trabajadas (alerta crítica)
    if (workData.horasActuales > 10) return true;
    
    // Mostrar si necesita aprobación
    if (workData.necesitaAprobacion && !workData.horasExtraAprobadas) return true;
    
    // No mostrar si todo está normal
    return false;
  };

  const getStatusColor = () => {
    if (!workData || !workData.sesionActiva) return '#6b7280';
    if (workData.horasExtra > 0 && !workData.horasExtraAprobadas) return '#ef4444';
    if (workData.horasExtra > 0 && workData.horasExtraAprobadas) return '#8b5cf6';
    if (workData.horasActuales > 6) return '#f59e0b';
    return '#10b981';
  };

  const getStatusMessage = () => {
    if (!workData || !workData.sesionActiva) {
      return '⏰ Sin sesión activa';
    }
    
    if (workData.estado === 'bloqueada') {
      return '🚫 Sesión bloqueada - Requiere aprobación';
    }
    
    if (workData.horasExtra > 0 && !workData.horasExtraAprobadas) {
      return `⚠️ Horas extra: ${formatTime(workData.horasExtra)} - Pendiente aprobación`;
    }
    
    if (workData.horasExtra > 0 && workData.horasExtraAprobadas) {
      return `✅ Horas extra aprobadas: ${formatTime(workData.horasExtra)}`;
    }
    
    if (workData.horasActuales >= 7.5) {
      return '⏳ Cerca del límite de 8 horas';
    }
    
    return '✅ Jornada normal';
  };

  if (!['operador', 'supervisor'].includes(user?.role)) {
    return null; // Solo mostrar para operadores y supervisores
  }

  // Solo mostrar el widget cuando hay irregularidades que requieren atención
  if (!loading && !hasIrregularities()) {
    return null; // No mostrar si todo está normal
  }

  if (loading) {
    return (
      <div className="work-time-widget loading">
        <div className="spinner"></div>
        <span>Cargando tiempo...</span>
      </div>
    );
  }

  if (!workData) {
    return (
      <div className="work-time-widget error">
        <h3>⏰ Control de Tiempo</h3>
        <p>Error cargando información</p>
      </div>
    );
  }

  return (
    <div className="work-time-widget">
      <div className="widget-header">
        <h3>⏰ Control de Tiempo</h3>
        <div className="current-time">
          {currentTime.toLocaleTimeString('es-AR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>

      <div className="time-display">
        <div className="main-time" style={{ color: getStatusColor() }}>
          {workData.sesionActiva 
            ? formatTime(workData.horasActuales)
            : '0h 0m'
          }
        </div>
        <div className="time-label">
          {workData.sesionActiva ? 'Tiempo trabajado hoy' : 'Sin sesión activa'}
        </div>
      </div>

      {workData.sesionActiva && (
        <>
          <div className="progress-bar">
            <div className="progress-label">
              <span>Progreso jornada (8h objetivo)</span>
              <span>{((workData.horasActuales / 8) * 100).toFixed(0)}%</span>
            </div>
            <div className="progress-track">
              <div 
                className="progress-fill"
                style={{
                  width: `${Math.min((workData.horasActuales / 8) * 100, 100)}%`,
                  backgroundColor: getStatusColor()
                }}
              />
              {workData.horasActuales > 8 && (
                <div 
                  className="progress-extra"
                  style={{
                    width: `${Math.min(((workData.horasActuales - 8) / 4) * 100, 100)}%`,
                    backgroundColor: workData.horasExtraAprobadas ? '#8b5cf6' : '#ef4444'
                  }}
                />
              )}
            </div>
          </div>

          <div className="time-breakdown">
            <div className="time-item">
              <span className="time-value">{formatTime(workData.horasRegulares)}</span>
              <span className="time-desc">Regulares</span>
            </div>
            {workData.horasExtra > 0 && (
              <div className="time-item extra">
                <span className="time-value" style={{ 
                  color: workData.horasExtraAprobadas ? '#8b5cf6' : '#ef4444' 
                }}>
                  {formatTime(workData.horasExtra)}
                </span>
                <span className="time-desc">
                  {workData.horasExtraAprobadas ? 'Extra ✅' : 'Extra ⏳'}
                </span>
              </div>
            )}
          </div>

          {workData.loginTime && (
            <div className="session-info">
              <small>
                📅 Inicio: {new Date(workData.loginTime).toLocaleString('es-AR')}
              </small>
            </div>
          )}
        </>
      )}

      <div className="status-message" style={{ color: getStatusColor() }}>
        {getStatusMessage()}
      </div>

      <style jsx>{`
        .work-time-widget {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 20px;
          border: 1px solid #e5e7eb;
        }

        .work-time-widget.loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px;
          color: #6b7280;
        }

        .work-time-widget.error {
          border-left: 4px solid #ef4444;
          background-color: #fef2f2;
        }

        .widget-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .widget-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1f2937;
        }

        .current-time {
          background: #f3f4f6;
          color: #374151;
          padding: 4px 12px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .time-display {
          text-align: center;
          margin-bottom: 20px;
        }

        .main-time {
          font-size: 48px;
          font-weight: 700;
          line-height: 1;
          font-family: 'Monaco', 'Menlo', monospace;
          margin-bottom: 8px;
        }

        .time-label {
          font-size: 14px;
          color: #6b7280;
        }

        .progress-bar {
          margin-bottom: 16px;
        }

        .progress-label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .progress-track {
          width: 100%;
          height: 8px;
          background-color: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
          position: relative;
        }

        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: all 0.3s ease;
        }

        .progress-extra {
          position: absolute;
          top: 0;
          left: 100%;
          height: 100%;
          border-radius: 4px;
          transition: all 0.3s ease;
        }

        .time-breakdown {
          display: flex;
          justify-content: space-around;
          margin-bottom: 16px;
          padding: 12px 0;
          background: #f9fafb;
          border-radius: 8px;
        }

        .time-item {
          text-align: center;
        }

        .time-value {
          display: block;
          font-size: 20px;
          font-weight: 700;
          color: #10b981;
          font-family: 'Monaco', 'Menlo', monospace;
          line-height: 1;
        }

        .time-desc {
          display: block;
          font-size: 11px;
          color: #6b7280;
          text-transform: uppercase;
          font-weight: 600;
          margin-top: 4px;
        }

        .session-info {
          text-align: center;
          margin-bottom: 12px;
        }

        .session-info small {
          color: #6b7280;
          font-size: 11px;
        }

        .status-message {
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          padding: 8px 12px;
          background: rgba(107, 114, 128, 0.1);
          border-radius: 8px;
        }

        .spinner {
          border: 2px solid #f3f4f6;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WorkTimeWidget;